import React, { createContext, useContext, useEffect, useState } from "react";
import { IWizardReportDetailsResponse } from "../../../../shared/contracts/IWizardReportDetailsResponse";
import { IWizardData } from "../contracts/IWizardData";
import IReportAvailableColumn from "../../../../shared/contracts/IReportAvailableColumn";
import IDataSourceProps from "../../../../shared/contracts/IDataSourceProps";
import { IReportDataOptions } from "../../../../shared/contracts/IReportDataOptions";
import { IWizardPivotData } from "../contracts/IWizardPivotData";
import useHelpers from "../../../../shared/services/helpers/useHelpers.hook";
import { IReportPivotSelection } from "../../../../shared/contracts/IPivotSelection";
import IReportMiscInformation from "../../../../shared/contracts/IReportMiscInformation";
import { IReportStyleInformation } from "../../../../shared/contracts/IReportStyleInformation";

export interface IWizardReportData {
  isNewReport: boolean;
  isEditReport: boolean;
  /**
   * used to initialize
   * @forNew sets empty values for creating new Report
   * @forUpdate sets the current data values from the api for Editing
   */
  initializeReportData: (value: IWizardReportDetailsResponse | null) => void;
  /**
   * @forNew clears all edited values
   * @forUpdate resets back to the original api values
   */
  resetReportData: () => void;
  /**
   * Used to update the current data
   */
  applyReportData: (value: Partial<IWizardData>) => void;
  /**
   * Current data for saving
   */
  wizardData: IWizardData; // | null;
  /**
   * Clears all values from the current data
   */
  clearReportData: () => void;
  /**
   * for the fields-grid's column options
   */
  availableColumns: IReportAvailableColumn[];
  setAvailableColumns: React.Dispatch<React.SetStateAction<IReportAvailableColumn[]>>;


  filterOptions: IReportDataOptions | null;
  setFilterOptions: React.Dispatch<React.SetStateAction<IReportDataOptions | null>>;

  groupByOptions: IReportDataOptions | null;
  setGroupByOptions: React.Dispatch<React.SetStateAction<IReportDataOptions | null>>;

  formatOptions: IReportDataOptions | null;
  setFormatOptions: React.Dispatch<React.SetStateAction<IReportDataOptions | null>>;

  pivotData: IWizardPivotData | null;
  setPivotData: React.Dispatch<React.SetStateAction<IWizardPivotData | null>>;
  //--
  availableDataSources: IDataSourceProps[];
  cacheAvailableDataSources: (value: IDataSourceProps[]) => void;
}

const transformToFormData = (apiData: IWizardReportDetailsResponse | null): IWizardData => ({
  reportId: apiData?.id || 0,
  reportCode: apiData?.reportCode || '',
  owner: apiData?.owner || 'Everyone',
  bucket: apiData?.bucket || 'Public',
  reportName: apiData?.reportName || '',
  reportDescription: apiData?.reportDescription || '',
  permissions: apiData?.permissions || '',
  category: apiData?.reportCategory || '',
  emailRecipients: apiData?.recipients || '',
  exportData: apiData?.exportFormat || '',
  datasourceGuid: apiData?.datasourceGuid || '',
  datasourceGuids: apiData?.datasourceGuid ? [apiData?.datasourceGuid] : [],
  scheduleDate: apiData?.scheduleDate ? new Date(apiData.scheduleDate) : new Date(),
  scheduleFrequency:apiData?.scheduleFrequency||'',
  reportSpecification: {
    fetchDataWithExistingMetadata: false,
    filterCriterias: apiData?.selectedFilters || [],
    columnSelections: apiData?.selectedColumns || [],
    pivotSelections: apiData?.pivotSelections || [],
    miscInformation: apiData?.miscInformation || {} as IReportMiscInformation,
    styleInformation: apiData?.styleInformation || {} as IReportStyleInformation,
  },
});
/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
function useWizardReportDataHook(isNewReport: boolean): IWizardReportData {

  const { areDifferent } = useHelpers();
  const [apiReportData, setApiReportData] = useState<IWizardReportDetailsResponse | null>(null);
  const [wizardData, setWizardData] = useState<IWizardData>(transformToFormData(null));
  const [availableColumns, setAvailableColumns] = useState<IReportAvailableColumn[]>([]);
  const [availableDataSources, setAvailableDataSources] = useState<IDataSourceProps[]>([]);

  const [filterOptions, setFilterOptions] = useState<IReportDataOptions | null>(null);
  const [groupByOptions, setGroupByOptions] = useState<IReportDataOptions | null>(null);
  const [formatOptions, setFormatOptions] = useState<IReportDataOptions | null>(null);
  const [pivotData, setPivotData] = useState<IWizardPivotData | null>(null);
  const [isPivotInitialized, setIsPivotInitialized] = useState(false);

  const transformPivotData = (pivotSelections: IReportPivotSelection[]) => {
    let _pivotData: IWizardPivotData | null = null;
    if (pivotSelections.length > 0 && pivotSelections[0].columnName) {
      // build parent column
      _pivotData = {
        parentColumn: availableColumns.find(x => x.columnName === pivotSelections[0].columnName) || null,
        parentGroupByFunction: groupByOptions?.options.find(x => x.optionValue === pivotSelections[0].aggregateFunction) || null,
        childColumns: []
      };

      // build child columns
      for (let index = 1; index < pivotSelections.length; index++) {
        const childPivotSrc = pivotSelections[index];
        const childCol = availableColumns.find(x => x.columnName === childPivotSrc.columnName) || null;
        if (childCol) {
          const formatOption = formatOptions?.options.find(x => x.optionValue === childPivotSrc.formatString) || null;
          _pivotData.childColumns?.push({
            childColumnName: childCol.columnName,
            childColumnDescription: childPivotSrc.columnDescription,
            childColumnGroupByValue: childPivotSrc.aggregateFunction,
            childColumnFormatValue: formatOption?.optionValue || null
          })
        }
      }
    }

    // update
    if (areDifferent(pivotData, _pivotData)) {
      setPivotData(_pivotData);
      setIsPivotInitialized(true);
    }
  }

  useEffect(() => {
    if (pivotData?.parentColumn === null) {
      transformPivotData([]);
    } else if (!isPivotInitialized && availableColumns.length && wizardData.reportSpecification.pivotSelections.length) {
      transformPivotData(wizardData.reportSpecification.pivotSelections);
    }
  }, [isPivotInitialized, availableColumns, wizardData, pivotData?.parentColumn])


  const initializeReportData = (apiData: IWizardReportDetailsResponse | null) => {
    setIsPivotInitialized(false);
    setPivotData(null);
    const transformed = transformToFormData(apiData);
    setWizardData(transformed);
    setApiReportData(apiData);
  };

  const resetReportData = () => {
    const resetData = isNewReport ? transformToFormData(null) : transformToFormData(apiReportData);
    setIsPivotInitialized(false);
    setPivotData(null);
    setWizardData(resetData);
    transformPivotData(isNewReport ? [] : resetData.reportSpecification.pivotSelections);
  };

  const clearReportData = () => { }

  const applyReportData = (updates: Partial<IWizardData>) => {
    setWizardData((prev) => ({
      ...prev,
      ...updates,
      reportSpecification: {
        ...prev.reportSpecification,
        ...updates.reportSpecification,
      },
    }));
  };

  const cacheAvailableDataSources = (value: IDataSourceProps[]) => {
    setAvailableDataSources(value);
  }



  return {
    isNewReport,
    isEditReport: !isNewReport,
    wizardData,
    initializeReportData,
    resetReportData,
    applyReportData,
    clearReportData,
    availableColumns,
    //---
    filterOptions, setFilterOptions,
    groupByOptions, setGroupByOptions,
    formatOptions, setFormatOptions,
    // ---
    pivotData, setPivotData,
    availableDataSources,
    cacheAvailableDataSources,
    setAvailableColumns,
  };
}

const WizardReportDataContext = createContext<IWizardReportData>({} as IWizardReportData);

export const WizardReportDataContextProvider = ({
  children,
  isNewReport,
}: {
  children: React.ReactNode;
  isNewReport: boolean;
}) => {
  const wizardReportDataHook = useWizardReportDataHook(isNewReport);
  return (
    <WizardReportDataContext.Provider value={wizardReportDataHook}>
      {children}
    </WizardReportDataContext.Provider>
  );
};

const useWizardReportDataContext = () => useContext(WizardReportDataContext);
export default useWizardReportDataContext;